import React from 'react';

/**
 * SmartPagination - Intelligent pagination with ellipsis
 * Shows window of pages around current page, with ... separators
 * Props: page, pages, onPageChange
 */
const SmartPagination = ({ page, pages, onPageChange, total, limit }) => {
    if (!pages || pages <= 1) return null;

    const getPageNumbers = () => {
        const delta = 2; // pages to show on each side of current
        const range = [];
        const rangeWithDots = [];

        // Always show first and last pages
        const left = Math.max(2, page - delta);
        const right = Math.min(pages - 1, page + delta);

        for (let i = left; i <= right; i++) range.push(i);

        // Build final array
        // First page always
        rangeWithDots.push(1);

        // Dots if there's a gap after 1
        if (left > 2) rangeWithDots.push('...');

        // Middle pages
        range.forEach(p => rangeWithDots.push(p));

        // Dots if there's a gap before last
        if (right < pages - 1) rangeWithDots.push('...');

        // Last page always (if more than 1 page)
        if (pages > 1) rangeWithDots.push(pages);

        return rangeWithDots;
    };

    const pageNumbers = getPageNumbers();
    const start = (page - 1) * (limit || 20) + 1;
    const end = Math.min(page * (limit || 20), total || 0);

    const btnBase = {
        minWidth: '36px', height: '36px', borderRadius: '8px',
        border: '1px solid var(--border)',
        background: 'white', color: 'var(--text-secondary)',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '13px', fontWeight: '600', padding: '0 10px',
        transition: 'all 0.15s',
    };

    const btnActive = {
        ...btnBase,
        background: 'var(--primary-700)', color: 'white',
        border: '1px solid var(--primary-700)',
        boxShadow: '0 2px 8px rgba(26,58,92,0.3)',
    };

    const btnDisabled = {
        ...btnBase,
        opacity: 0.4, cursor: 'not-allowed',
    };

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 20px',
            borderTop: '1px solid var(--border)',
            background: 'var(--surface-2)',
            flexWrap: 'wrap',
            gap: '10px',
        }}>
            {/* Info */}
            {total !== undefined && (
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500' }}>
                    {start}–{end} sur <strong>{total}</strong> résultats
                </span>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {/* Prev */}
                <button
                    style={page === 1 ? btnDisabled : btnBase}
                    onClick={() => page > 1 && onPageChange(page - 1)}
                    disabled={page === 1}
                    title="Page précédente"
                >
                    ‹
                </button>

                {pageNumbers.map((p, i) => (
                    p === '...'
                        ? (
                            <span key={`dots-${i}`} style={{ padding: '0 4px', color: 'var(--text-muted)', fontSize: '13px' }}>
                                ···
                            </span>
                        ) : (
                            <button
                                key={p}
                                style={p === page ? btnActive : btnBase}
                                onClick={() => p !== page && onPageChange(p)}
                                onMouseEnter={e => { if (p !== page) { e.currentTarget.style.background = 'var(--surface-3)'; e.currentTarget.style.borderColor = 'var(--primary-300)'; } }}
                                onMouseLeave={e => { if (p !== page) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = 'var(--border)'; } }}
                            >
                                {p}
                            </button>
                        )
                ))}

                {/* Next */}
                <button
                    style={page === pages ? btnDisabled : btnBase}
                    onClick={() => page < pages && onPageChange(page + 1)}
                    disabled={page === pages}
                    title="Page suivante"
                >
                    ›
                </button>
            </div>
        </div>
    );
};

export default SmartPagination;
